import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { sortableElement } from 'react-sortable-hoc';

import { ENTITY_TYPES } from 'common';

import { activeChapter } from 'store/slices/learningCommunity';

import CheckBox from 'style-guide/CheckBox';

import VolumeCategories from 'Components/Categories/VolumeCategories';
import { useUpdateLearningCommunitiesBookmarkMutation } from 'store/services/learningCommunities';
import ChapterDragHandle from './ChapterDragHandle';
import ChapterBoxWrapper from './style/ChapterBoxWrapper';

const { LEARNING_COMMUNITY } = ENTITY_TYPES;

const SortableChapterItem = sortableElement(({ data, isLast, isPreview }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { id: chapterId, volumeId, name, chapterCategoriesStatus, showChapter } = data;

  const { pathname } = useLocation();
  const { id, urlName, category, chapterId: activeChapterId, role } = useParams();

  const setActiveChapter = useCallback((params) => dispatch(activeChapter(params)), [dispatch]);

  const [updateBookmark] = useUpdateLearningCommunitiesBookmarkMutation();

  const activeCheckboxRef = useRef();

  const isCheckedChapter = chapterId === +activeChapterId || isPreview;

  const bookmarkData = useMemo(
    () => ({
      courseId: id,
      chapterId,
      volumeId,
      section: isPreview ? 'survey' : category,
    }),
    [category, chapterId, id, isPreview, volumeId]
  );

  const getUrl = useCallback(
    (value) => `/${LEARNING_COMMUNITY.key}/${id}/${urlName}/${role}/${chapterId}/${value}`,
    [chapterId, id, role, urlName]
  );

  const changeCategory = useCallback(
    (value) => {
      const url = getUrl(value);
      if (pathname !== url) {
        updateBookmark(bookmarkData);
        navigate(url);
      }
    },
    [getUrl, pathname, updateBookmark, bookmarkData, navigate]
  );

  useEffect(() => {
    if (isCheckedChapter) {
      activeCheckboxRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isCheckedChapter]);

  useEffect(() => {
    if (isCheckedChapter) {
      setActiveChapter(data);
    }
  }, [isCheckedChapter, data, setActiveChapter]);

  const levelPassedCourse = chapterCategoriesStatus?.passed === 1 ? 'half-checked' : null;

  return (
    <ChapterBoxWrapper className='mv--12'>
      <div className='d-flex align-items-center chapter-name-box pl--16 mb--16' ref={activeCheckboxRef}>
        <CheckBox disabled={isPreview} checked={chapterCategoriesStatus?.passed} className={levelPassedCourse} />
        <ChapterDragHandle
          name={name}
          isLast={isLast}
          chapterId={chapterId}
          volumeId={volumeId}
          isCheckedChapter={isCheckedChapter}
          showChapter={showChapter}
          isPreview={isPreview}
        />
      </div>
      {isCheckedChapter ? (
        <VolumeCategories
          changeCategory={changeCategory}
          activeCategory={isCheckedChapter && category}
          options={data.chapterSection}
          onClick={(e) => e.stopPropagation()}
          isPreview={isPreview}
        />
      ) : null}
    </ChapterBoxWrapper>
  );
});

export default SortableChapterItem;
